import React, { useState, useEffect } from 'react';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { api } from '../api';
import { CommentItem } from './CommentItem';
import * as Sentry from '@sentry/browser';

export function CommentsModal({ request, onClose, onCommentCountChange }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchComments = async () => {
      try {
        const data = await api.getComments(request.id);
        setComments(data);
      } catch (error) {
        console.error('Error fetching comments:', error);
        Sentry.captureException(error);
      } finally {
        setLoading(false);
      }
    };

    fetchComments();
  }, [request.id]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!user || !newComment.trim() || submitting) return;

    setSubmitting(true);
    try {
      const comment = await api.addComment(request.id, newComment.trim());
      
      // The comment now includes userEmail and proper field names from the backend
      const newCommentWithReplies = {
        ...comment,
        replies: [] // Initialize empty replies array
      };
      
      setComments(prev => [newCommentWithReplies, ...prev]);
      setNewComment('');
      
      // Notify parent component about the comment count change
      // Since this is a top-level comment, increment the count by 1
      if (onCommentCountChange) {
        onCommentCountChange(1);
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      Sentry.captureException(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVoteComment = async (commentId, voteType) => {
    if (!user) return;

    try {
      const result = await api.voteComment(commentId, voteType);
      
      // Update the comment in the list
      setComments(prev => prev.map(comment => {
        if (comment.id === commentId) {
          return {
            ...comment,
            userVote: result.userVote,
            upvotes: result.upvotes,
            downvotes: result.downvotes
          };
        }
        
        // Also check replies
        if (comment.replies && comment.replies.length > 0) {
          const updatedReplies = comment.replies.map(reply => {
            if (reply.id === commentId) {
              return {
                ...reply,
                userVote: result.userVote,
                upvotes: result.upvotes,
                downvotes: result.downvotes
              };
            }
            return reply;
          });
          
          return {
            ...comment,
            replies: updatedReplies
          };
        }
        
        return comment;
      }));
    } catch (error) {
      console.error('Error voting on comment:', error);
      Sentry.captureException(error);
    }
  };

  const handleAddReply = async (parentCommentId, content) => {
    if (!user || !content.trim()) return;

    try {
      const reply = await api.addReply(parentCommentId, content.trim());
      
      // The reply now includes userEmail and proper field names from the backend
      setComments(prev => prev.map(comment => {
        if (comment.id === parentCommentId) {
          return {
            ...comment,
            replies: [...(comment.replies || []), reply]
          };
        }
        return comment;
      }));
    } catch (error) {
      console.error('Error adding reply:', error);
      Sentry.captureException(error);
      throw error;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-4xl sm:w-full max-h-[85vh] sm:max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex justify-between items-start">
            <div className="flex-1 pr-4">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 line-clamp-2">{request.title}</h2>
              <p className="text-gray-700 text-sm line-clamp-3">{request.description}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 cursor-pointer p-1 flex-shrink-0"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Comments List */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {loading ? (
            <div className="text-center text-gray-500 py-8">
              <div className="w-8 h-8 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto mb-2"></div>
              Loading comments...
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <div className="text-gray-900 font-medium mb-2">No comments yet</div>
              <div className="text-sm">Be the first to comment!</div>
            </div>
          ) : (
            <div className="space-y-6">
              {comments.map((comment) => (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  onVote={handleVoteComment}
                  onReply={handleAddReply}
                  currentUser={user}
                />
              ))}
            </div>
          )}
        </div>

        {/* Comment Form */}
        {user && (
          <div className="p-4 sm:p-6 border-t border-gray-200 flex-shrink-0">
            <form onSubmit={handleSubmitComment} className="space-y-3">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add your comment..."
                rows={3}
                className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent box-border text-gray-900 text-sm sm:text-base resize-none"
                maxLength={1000}
              />
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">{newComment.length}/1000 characters</span>
                <button
                  type="submit"
                  disabled={!newComment.trim() || submitting}
                  className="bg-blue-600 text-white px-4 py-2 sm:px-6 sm:py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium cursor-pointer transition-colors text-sm sm:text-base flex items-center"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Posting...
                    </>
                  ) : (
                    'Post Comment'
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}